/**
 * Theme-mode capability — the single source of truth for light / dark / system.
 *
 * The mode is a user preference (persisted); the *resolved* theme is what actually
 * paints ('system' resolves against the OS `prefers-color-scheme`). Applying a theme
 * means setting `data-theme` on <html>, which drives BOTH the CSS role-token remap
 * (`[data-theme="dark"]` in globals.css) and the ECharts theme resolver — so every
 * surface, class-based or imperative, re-themes off one switch.
 *
 * Default is `light`: without an explicit choice the app stays on today's look, so
 * dark stays opt-in until it's primed. The toggle that changes this is gated behind
 * `THEME_TOGGLE_FLAG`. Pure logic lives here; the React binding is in `ThemeProvider`.
 */

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_TOGGLE_FLAG = 'theme-toggle';

const STORAGE_KEY = 'vertice-theme';
export const DARK_QUERY = '(prefers-color-scheme: dark)';

function prefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(DARK_QUERY).matches;
}

export function resolveTheme(mode: ThemeMode): ResolvedTheme {
  if (mode === 'system') return prefersDark() ? 'dark' : 'light';
  return mode;
}

export function readStoredMode(): ThemeMode {
  try {
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    return stored === 'dark' || stored === 'system' || stored === 'light' ? stored : 'light';
  } catch {
    // Some browsers throw on *any* localStorage access (e.g. Safari "Block all cookies") —
    // even `typeof`/`getItem`. This runs before first paint (initThemeMode), so never let it
    // crash boot; default to light. Symmetric with storeMode's guard.
    return 'light';
  }
}

export function storeMode(mode: ThemeMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* storage unavailable (private mode) — the in-memory choice still applies */
  }
}

/**
 * Apply the resolved theme to <html>. `data-theme` is the switch both the CSS remap
 * and the chart theme resolver read; `color-scheme` keeps native controls, form
 * fields and scrollbars in sync.
 */
export function applyResolvedTheme(resolved: ResolvedTheme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.setAttribute('data-theme', resolved);
  root.style.colorScheme = resolved;
}

/**
 * Set the initial theme before React renders, so there is no flash of the wrong
 * theme on load. Call once from the entry module, ahead of `createRoot`.
 */
export function initThemeMode(): void {
  applyResolvedTheme(resolveTheme(readStoredMode()));
}
